import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { ConfigInitializer, WindowRef } from '@spartacus/core';
import { forkJoin, firstValueFrom, map } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { Request } from 'express';

import { ConfigurationService } from '../../services/configuration.service';
import { BaseStoreService } from '../../services/base-store.service';
import { WebCommonConfig } from '../configs/web-common.config';
import { SERVER_REQUEST } from '../../constants';
import { CustomBaseStore } from '../../models';
import { getDomain } from '../../utils';

@Injectable()
export class AuthenticationConfigInitializer implements ConfigInitializer {
  scopes = ['authentication'];

  constructor(
    private configurationService: ConfigurationService,
    private baseStoreService: BaseStoreService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Optional() @Inject(SERVER_REQUEST) private request: Request,
    @Optional() private windowRef: WindowRef
  ) {}

  static getExpId(baseStore: CustomBaseStore, config: WebCommonConfig, language?: string): string {
    const { authentication } = config;

    if (authentication?.forcedExpId) {
      return authentication.forcedExpId;
    }

    let expIdLang = language ? baseStore.languages.find((lang) => lang.isocode === language)?.langue_SF : undefined;

    expIdLang ??= baseStore.defaultLanguage.langue_SF;

    return `${authentication.expIdBrand}${baseStore?.market}${expIdLang}${authentication?.expIdSuffix}`;
  }

  static getLoginUrl(baseStore: CustomBaseStore, config: WebCommonConfig, language?: string): string {
    return `/services/oauth2/authorize/expid_${AuthenticationConfigInitializer.getExpId(baseStore, config, language)}`;
  }

  configFactory(): Promise<any> {
    return firstValueFrom(
      forkJoin([
        this.configurationService.getConfig(),
        this.baseStoreService.getBaseStore()
      ]).pipe(
        map(([config, baseStore]) => {
          const host = isPlatformBrowser(this.platformId) ? this.windowRef.nativeWindow.location.hostname : '';

          const authBaseUrl = config.authentication?.forcedBaseUrl
            ? config.authentication?.forcedBaseUrl
            : this.generateAuthBaseUrl(config.authentication?.subDomain);

          return {
            authentication: {
              client_id: config.authentication?.clientId,
              client_secret: undefined,
              baseUrl: `${authBaseUrl}`,
              loginUrl: AuthenticationConfigInitializer.getLoginUrl(baseStore, config),
              logoutUrl: '/services/auth/idp/oidc/logout',
              tokenEndpoint: '/services/oauth2/token',
              revokeEndpoint: `https://${host}/api/oauth2/revoke`,
              userinfoEndpoint: '/services/oauth2/userinfo',
              OAuthLibConfig: {
                redirectUri: `https://${host}/redirect_rcu`,
                scope: 'web api',
                responseType: 'token'
              }
            }
          };
        })
      )
    );
  }

  private generateAuthBaseUrl(subDomain: string): string {
    const host = isPlatformBrowser(this.platformId) ? this.windowRef.nativeWindow.location.hostname : this.request.hostname;
    const siteDomain = getDomain(host);

    return `https://${subDomain}.${siteDomain}`;
  }
}
